/* Baseline templates (designs/product-baselines.md BL1–BL6, BL21): the list
   anyone signed in reads, and the editor an Admin uses to make a new kind of
   baseline — its roles, columns, grouping and approval flow — without a
   code change. A save is a new template revision; baselines already
   drafted keep the revision they were drafted on. */
import { useEffect, useState, type JSX } from "react";
import {
  COLUMN_TYPES,
  COLUMN_TYPE_LABEL,
  STAGE_RULES,
  STAGE_RULE_LABEL,
  finalizeKeys,
  type Column,
  type ColumnType,
  type Stage,
  type StageRule,
  type TemplateBody,
  type TemplateRevision,
} from "../../../../shared/baselines";
import { useAppState } from "../../shell/store";
import { getTemplates, message, postTemplate, postTemplateRevision } from "./api";

const EMPTY: TemplateBody = {
  name: "",
  description: "",
  roles: [{ key: "new-1", label: "Owner" }],
  ownerRole: "new-1",
  columns: [{ key: "new-2", label: "Item", type: "text", required: true, estimateAllowed: false, options: [] }],
  grouping: null,
  stages: [{ key: "new-3", name: "Approve", roleKeys: ["new-1"], rule: "all" }],
};

const bodyOf = (t: TemplateRevision): TemplateBody => ({
  name: t.name,
  description: t.description,
  roles: t.roles,
  ownerRole: t.ownerRole,
  columns: t.columns,
  grouping: t.grouping,
  stages: t.stages,
});

function Summary({ t }: { t: TemplateRevision }): JSX.Element {
  const label = (k: string): string => t.roles.find((r) => r.key === k)?.label ?? k;
  return (
    <div className="baseline-template-summary" data-template={t.templateId}>
      <p className="faint">{t.description}</p>
      <p>
        <b>Roles:</b> {t.roles.map((r) => r.label + (r.key === t.ownerRole ? " (owner)" : "")).join(", ")}
      </p>
      <p>
        <b>Columns:</b> {t.columns.map((c) => `${c.label} (${COLUMN_TYPE_LABEL[c.type].toLowerCase()}${c.required ? ", required" : ""})`).join(", ")}
      </p>
      {t.grouping && (
        <p>
          <b>Grouped by</b> {t.columns.find((c) => c.key === t.grouping!.columnKey)?.label}:{" "}
          {Object.entries(t.grouping.accountable)
            .map(([o, k]) => `${o} → ${label(k)}`)
            .join(", ")}
        </p>
      )}
      <p>
        <b>Approval:</b>{" "}
        {t.stages.map((s, i) => `${i + 1}. ${s.name} — ${s.roleKeys.map(label).join(", ")} (${STAGE_RULE_LABEL[s.rule].toLowerCase()})`).join(" · ")}
      </p>
    </div>
  );
}

export function TemplateEditor({
  start,
  onCancel,
  onSaved,
}: {
  /* null = a new template */
  start: TemplateRevision | null;
  onCancel: () => void;
  onSaved: () => void;
}): JSX.Element {
  const [body, setBody] = useState<TemplateBody>(start ? bodyOf(start) : EMPTY);
  const [fresh, setFresh] = useState<Set<string>>(new Set(start ? [] : ["new-1", "new-2", "new-3"]));
  const [seq, setSeq] = useState(4);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const set = (patch: Partial<TemplateBody>): void => setBody({ ...body, ...patch });
  const newKey = (): string => {
    const key = `new-${seq}`;
    setSeq(seq + 1);
    setFresh(new Set([...fresh, key]));
    return key;
  };
  const choiceColumns = body.columns.filter((c) => c.type === "choice");

  const setColumn = (i: number, c: Column): void => {
    const columns = body.columns.map((x, j) => (j === i ? c : x));
    /* a grouping on a column that stopped being a pick-list, or lost an
       option, follows the column */
    let grouping = body.grouping;
    if (grouping && grouping.columnKey === c.key) {
      grouping = c.type !== "choice" ? null : { ...grouping, accountable: Object.fromEntries(c.options.map((o) => [o, grouping!.accountable[o] ?? body.ownerRole])) };
    }
    set({ columns, grouping });
  };
  const removeRole = (key: string): void => {
    const roles = body.roles.filter((r) => r.key !== key);
    set({
      roles,
      ownerRole: body.ownerRole === key ? (roles[0]?.key ?? "") : body.ownerRole,
      stages: body.stages.map((s) => ({ ...s, roleKeys: s.roleKeys.filter((k) => k !== key) })),
      grouping: body.grouping && {
        ...body.grouping,
        accountable: Object.fromEntries(Object.entries(body.grouping.accountable).map(([o, k]) => [o, k === key ? (roles[0]?.key ?? "") : k])),
      },
    });
  };

  const save = (): void => {
    setBusy(true);
    setError(null);
    /* typing "A, B, " leaves a blank option while the list is edited; it is
       dropped here, and the grouping follows the options that remain */
    const columns = body.columns.map((c) => ({ ...c, options: c.options.filter((o) => o !== "") }));
    const groupCol = columns.find((c) => c.key === body.grouping?.columnKey);
    const grouping = body.grouping && groupCol
      ? { ...body.grouping, accountable: Object.fromEntries(groupCol.options.map((o) => [o, body.grouping!.accountable[o] ?? body.ownerRole])) }
      : null;
    const final = finalizeKeys({ ...body, columns, grouping }, fresh);
    (start ? postTemplateRevision(start.templateId, start.revision, final) : postTemplate(final))
      .then(onSaved)
      .catch((e: unknown) => setError(message(e)))
      .finally(() => setBusy(false));
  };

  return (
    <section className="card baseline-template-editor">
      <h3>{start ? `Edit ${start.name} — saving makes revision ${start.revision + 1}` : "New template"}</h3>
      {start && <p className="faint">Baselines already drafted keep revision {start.revision}; they can move to the new one when they next change.</p>}
      <label className="field">
        <span className="field-label">Name</span>
        <input id="template-name" value={body.name} onChange={(e) => set({ name: e.target.value })} />
      </label>
      <label className="field">
        <span className="field-label">Description</span>
        <textarea id="template-description" value={body.description} onChange={(e) => set({ description: e.target.value })} />
      </label>

      <h4>Roles</h4>
      {body.roles.map((r, i) => (
        <div key={r.key} className="baseline-template-line" data-role-line={i + 1}>
          <input aria-label={`Role ${i + 1}`} value={r.label} onChange={(e) => set({ roles: body.roles.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)) })} />
          <label>
            <input type="radio" name="owner-role" checked={body.ownerRole === r.key} onChange={() => set({ ownerRole: r.key })} /> owner
          </label>
          <button className="btn ghost small" disabled={body.roles.length === 1} onClick={() => removeRole(r.key)}>
            Remove
          </button>
        </div>
      ))}
      <button className="btn ghost small" onClick={() => set({ roles: [...body.roles, { key: newKey(), label: "" }] })}>
        Add role
      </button>

      <h4>Columns</h4>
      {body.columns.map((c, i) => (
        <div key={c.key} className="baseline-template-line" data-column-line={i + 1}>
          <input aria-label={`Column ${i + 1}`} value={c.label} onChange={(e) => setColumn(i, { ...c, label: e.target.value })} />
          <select aria-label={`Column ${i + 1} type`} value={c.type} onChange={(e) => {
            const type = e.target.value as ColumnType;
            setColumn(i, { ...c, type, options: type === "choice" ? (c.options.length ? c.options : ["Option 1"]) : [] });
          }}>
            {COLUMN_TYPES.map((t) => (
              <option key={t} value={t}>
                {COLUMN_TYPE_LABEL[t]}
              </option>
            ))}
          </select>
          {c.type === "choice" && (
            <input
              aria-label={`Column ${i + 1} options`}
              placeholder="Options, comma-separated"
              value={c.options.join(", ")}
              onChange={(e) => setColumn(i, { ...c, options: e.target.value.split(",").map((o) => o.trim()).filter((o, k, all) => o !== "" || k === all.length - 1) })}
            />
          )}
          <label>
            <input type="checkbox" checked={c.required} onChange={(e) => setColumn(i, { ...c, required: e.target.checked })} /> required
          </label>
          <label>
            <input type="checkbox" checked={c.estimateAllowed} onChange={(e) => setColumn(i, { ...c, estimateAllowed: e.target.checked })} /> may be an estimate
          </label>
          <button
            className="btn ghost small"
            disabled={body.columns.length === 1}
            onClick={() => set({ columns: body.columns.filter((_, j) => j !== i), grouping: body.grouping?.columnKey === c.key ? null : body.grouping })}
          >
            Remove
          </button>
        </div>
      ))}
      <button
        className="btn ghost small"
        onClick={() => set({ columns: [...body.columns, { key: newKey(), label: "", type: "text", required: false, estimateAllowed: false, options: [] }] })}
      >
        Add column
      </button>

      <h4>Grouping and RACI</h4>
      <label className="field">
        <span className="field-label">Group rows by</span>
        <select
          id="template-grouping"
          value={body.grouping?.columnKey ?? ""}
          onChange={(e) => {
            const col = body.columns.find((c) => c.key === e.target.value);
            set({ grouping: col ? { columnKey: col.key, accountable: Object.fromEntries(col.options.map((o) => [o, body.ownerRole])) } : null });
          }}
        >
          <option value="">No grouping — the owner is accountable for every row</option>
          {choiceColumns.map((c) => (
            <option key={c.key} value={c.key}>
              {c.label || "(unnamed pick-list)"}
            </option>
          ))}
        </select>
      </label>
      {body.grouping &&
        Object.keys(body.grouping.accountable).map((o) => (
          <label key={o} className="field baseline-template-line">
            <span className="field-label">{o} rows: accountable</span>
            <select
              aria-label={`${o} rows: accountable`}
              value={body.grouping!.accountable[o]}
              onChange={(e) => set({ grouping: { ...body.grouping!, accountable: { ...body.grouping!.accountable, [o]: e.target.value } } })}
            >
              {body.roles.map((r) => (
                <option key={r.key} value={r.key}>
                  {r.label || "(unnamed role)"}
                </option>
              ))}
            </select>
          </label>
        ))}

      <h4>Approval flow</h4>
      <p className="faint">Stages run in order. The owner submits; each stage approves or rejects.</p>
      {body.stages.map((s: Stage, i) => (
        <div key={s.key} className="baseline-template-stage" data-stage-line={i + 1}>
          <input aria-label={`Stage ${i + 1}`} value={s.name} onChange={(e) => set({ stages: body.stages.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)) })} />
          <select aria-label={`Stage ${i + 1} rule`} value={s.rule} onChange={(e) => set({ stages: body.stages.map((x, j) => (j === i ? { ...x, rule: e.target.value as StageRule } : x)) })}>
            {STAGE_RULES.map((r) => (
              <option key={r} value={r}>
                {STAGE_RULE_LABEL[r]}
              </option>
            ))}
          </select>
          {body.roles.map((r) => (
            <label key={r.key}>
              <input
                type="checkbox"
                aria-label={`Stage ${i + 1}: ${r.label}`}
                checked={s.roleKeys.includes(r.key)}
                onChange={(e) =>
                  set({
                    stages: body.stages.map((x, j) =>
                      j === i ? { ...x, roleKeys: e.target.checked ? [...x.roleKeys, r.key] : x.roleKeys.filter((k) => k !== r.key) } : x,
                    ),
                  })
                }
              />{" "}
              {r.label || "(unnamed role)"}
            </label>
          ))}
          <button className="btn ghost small" disabled={body.stages.length === 1} onClick={() => set({ stages: body.stages.filter((_, j) => j !== i) })}>
            Remove
          </button>
        </div>
      ))}
      <button className="btn ghost small" onClick={() => set({ stages: [...body.stages, { key: newKey(), name: "", roleKeys: [], rule: "all" }] })}>
        Add stage
      </button>

      {error !== null && (
        <p className="capture-error" role="alert">
          {error}
        </p>
      )}
      <div className="form-actions">
        <button className="btn ghost" onClick={onCancel}>
          Cancel
        </button>
        <button className="btn" disabled={busy} onClick={save}>
          {start ? `Save as revision ${start.revision + 1}` : "Create template"}
        </button>
      </div>
    </section>
  );
}

export function TemplatesPanel({
  templates,
  admin,
  onChanged,
}: {
  templates: TemplateRevision[];
  admin: boolean;
  onChanged: () => void;
}): JSX.Element {
  const [editing, setEditing] = useState<TemplateRevision | "new" | null>(null);
  if (editing !== null)
    return (
      <TemplateEditor
        start={editing === "new" ? null : editing}
        onCancel={() => setEditing(null)}
        onSaved={() => {
          setEditing(null);
          onChanged();
        }}
      />
    );
  return (
    <section className="baseline-templates">
      <div className="issues-bar">
        <span className="faint">{admin ? "Templates define each kind of baseline and its approval flow." : "Only an Admin creates or changes templates."}</span>
        <span className="spacer" />
        {admin && (
          <button className="btn small" onClick={() => setEditing("new")}>
            New template
          </button>
        )}
      </div>
      {templates.length === 0 ? (
        <p className="empty">No templates yet.</p>
      ) : (
        templates.map((t) => (
          <section key={t.templateId} className="card">
            <h3>
              {t.name} <span className="faint">revision {t.revision}</span>
            </h3>
            <Summary t={t} />
            {admin && (
              <div className="form-actions">
                <button className="btn ghost small" onClick={() => setEditing(t)}>
                  Edit {t.name}
                </button>
              </div>
            )}
          </section>
        ))
      )}
    </section>
  );
}

/* The PDP section's Baseline templates tab (BL25): templates are an Admin
   catalog, like the PDP's own; anyone signed in reads them. */
export function BaselineTemplatesTab(): JSX.Element {
  const { state } = useAppState();
  const [templates, setTemplates] = useState<TemplateRevision[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const load = (): void => {
    getTemplates()
      .then(setTemplates)
      .catch((e: unknown) => setError(message(e)));
  };
  useEffect(load, []);
  return (
    <>
      {error !== null && (
        <p className="capture-error" role="alert">
          The baseline templates could not be read: {error}
        </p>
      )}
      {templates !== null && <TemplatesPanel templates={templates} admin={state.me.roles.includes("admin")} onChanged={load} />}
    </>
  );
}
